/**
 * Component Registry for Price Brothers Theme
 *
 * Extends the base preferred theme registry with Price Brothers specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-preferred/registry';
import { AccessibilityHero } from './components/AccessibilityHero';
import { AccessibilityStatement } from './components/AccessibilityStatement';
import { AmenitiesHero } from './components/AmenitiesHero';
import { AmenitiesList } from './components/AmenitiesList';
import { ApplyNowAccess } from './components/ApplyNowAccess';
import { ApplyNowHero } from './components/ApplyNowHero';
import { ContactHero } from './components/ContactHero';
import { ContactUs } from './components/ContactUs';
import { FaqHero } from './components/FaqHero';
import { FaqList } from './components/FaqList';
import { FeaturedFloorPlan } from './components/FeaturedFloorPlan';
import { FeaturedFloorPlanHero } from './components/FeaturedFloorPlanHero';
import { FloorPlansHero } from './components/FloorPlansHero';
import { FloorplansList } from './components/FloorplansList';
import { FloorplanShowcase } from './components/FloorplanShowcase';
import { Footer } from './components/Footer';
import { Gallery } from './components/Gallery';
import { GalleryHero } from './components/GalleryHero';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { HeroSolidBG } from './components/HeroSolidBG';
import { InteractiveMapHero } from './components/InteractiveMapHero';
import { NeighborhoodCallout } from './components/NeighborhoodCallout';
import { NeighborhoodHero } from './components/NeighborhoodHero';
import { NeighborhoodMap } from './components/NeighborhoodMap';
import { OneBedroomHero } from './components/OneBedroomHero';
import { PageBlurb } from './components/PageBlurb';
import { PetPolicy } from './components/PetPolicy';
import { PetPolicyHero } from './components/PetPolicyHero';
import { PreferredEmployerBenefits } from './components/PreferredEmployerBenefits';
import { PreferredEmployerCTA } from './components/PreferredEmployerCTA';
import { PreferredEmployerHero } from './components/PreferredEmployerHero';
import { PreferredEmployerList } from './components/PreferredEmployerList';
import { PrivacyPolicyHero } from './components/PrivacyPolicyHero';
import { PropertyOverview } from './components/PropertyOverview';
import { ResidentAccess } from './components/ResidentAccess';
import { ResidentAccessHero } from './components/ResidentAccessHero';
import { Reviews } from './components/Reviews';
import { ReviewsCallout } from './components/ReviewsCallout';
import { ReviewsHero } from './components/ReviewsHero';
import { ScheduleATour } from './components/ScheduleATour';
import { ScheduleATourCallout } from './components/ScheduleATourCallout';
import { ScheduleATourHero } from './components/ScheduleATourHero';
import { SisterProperties } from './components/SisterProperties';
import { SisterPropertiesHero } from './components/SisterPropertiesHero';
import { SitemapContent } from './components/SitemapContent';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';
import { SitemapHero } from './components/SitemapHero';
import { StudiosHero } from './components/StudiosHero';
import { ThankYouContent } from './components/ThankYouContent';
import { ThankYouHero } from './components/ThankYouHero';
import { ThreeBedroomsHero } from './components/ThreeBedroomsHero';
import { TownhomesHero } from './components/TownhomesHero';
import { TwoBedroomsHero } from './components/TwoBedroomsHero';
import { ViewAllFloorPlansCallout } from './components/ViewAllFloorPlansCallout';
import { VirtualTourEmbed } from './components/VirtualTourEmbed';
import { VirtualTourHero } from './components/VirtualTourHero';

/**
 * Price Brothers specific component overrides.
 * These components have been customized for the Price Brothers brand.
 */
const pricebrothersOverrides: Partial<ComponentRegistry> = {
  Header,
  Footer,
  Hero,
  HeroSolidBG,
  PageBlurb,
  AmenitiesList,
  PetPolicy,
  PropertyOverview,
  AccessibilityStatement,
  ApplyNowAccess,
  FaqHero,
  FaqList,
  SitemapContent,
  FeaturedFloorPlan,
  FeaturedFloorPlanHero,
  FloorPlansHero,
  FloorplansList,
  FloorplanShowcase,
  InteractiveMapHero,
  VirtualTourEmbed,
  VirtualTourHero,
  AmenitiesHero,
  PetPolicyHero,
  PreferredEmployerHero,
  PreferredEmployerBenefits,
  PreferredEmployerList,
  PreferredEmployerCTA,
  Gallery,
  GalleryHero,
  NeighborhoodCallout,
  NeighborhoodHero,
  NeighborhoodMap,
  OneBedroomHero,
  ContactHero,
  ContactUs,
  ResidentAccess,
  ResidentAccessHero,
  ScheduleATour,
  ScheduleATourCallout,
  ScheduleATourHero,
  SisterProperties,
  SisterPropertiesHero,
  Reviews,
  ReviewsCallout,
  ReviewsHero,
  PrivacyPolicyHero,
  SitemapHero,
  AccessibilityHero,
  ApplyNowHero,
  ThankYouContent,
  ThankYouHero,
  TwoBedroomsHero,
  ThreeBedroomsHero,
  SpecialOfferBanner,
  StudiosHero,
  TownhomesHero,
  ViewAllFloorPlansCallout,
};

/**
 * Final merged registry for Price Brothers properties.
 * Inherits all components from the preferred theme, with Price Brothers overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...pricebrothersOverrides,
};

export default registry;
